import { Intl, Temporal, toTemporalInstant as toTemporalInstantPolyfill } from '@js-temporal/polyfill';
import { z } from 'zod';

import { assertNever } from './assertNever';

// This central reexport allows us to swap polyfills if we have any reason to
// (there are a couple out there in the wild)
export { Temporal, Intl };

export const BEGINNING_OF_DAY = Temporal.PlainTime.from('00:00');
export const END_OF_DAY = BEGINNING_OF_DAY.subtract({ microseconds: 1 });

export const zodTemporalInstant = z
  .instanceof(Temporal.Instant)
  .or(z.date())
  .or(z.string())
  .transform((d) =>
    d instanceof Temporal.Instant
      ? d
      : d instanceof Date
        ? Temporal.Instant.from(d.toISOString())
        : Temporal.Instant.from(d),
  )
  .meta({
    // `type: 'string'` keeps the REST data-export contract a string (values are ISO instants);
    // `lakeType: 'timestamp'` tells the data lake to store a first-class UTC TIMESTAMP instead.
    type: 'string',
    lakeType: 'timestamp',
    example: '2025-07-08T22:32:15.699Z',
    description: 'An instant in time (UTC), ISO-8601.',
  });

const stringToTemporalPlainDate = (d: string): Temporal.PlainDate => {
  if (!d.includes('T')) {
    return Temporal.PlainDate.from(d);
  }
  return Temporal.PlainDate.from(d.split('T')[0]);
};

export const zodTemporalPlainDate = z
  .instanceof(Temporal.PlainDate)
  .or(z.date())
  .or(z.string())
  .transform((d) =>
    d instanceof Temporal.PlainDate ? d : stringToTemporalPlainDate(d instanceof Date ? d.toISOString() : d),
  )
  .meta({
    type: 'string',
    lakeType: 'date',
    example: '2025-07-08',
    description: 'A date with calendar year, month, and day.',
  });

export const zodTemporalPlainTime = z
  .instanceof(Temporal.PlainTime)
  .or(z.string().transform((s) => Temporal.PlainTime.from(s)));

export const zodTemporalZonedDateTime = z
  .instanceof(Temporal.ZonedDateTime)
  .or(z.string())
  .pipe(z.transform((s) => Temporal.ZonedDateTime.from(s)))
  .meta({
    type: 'string',
    lakeType: 'timestamp',
    example: '2025-07-08T22:32:15.699935523+00:00[UTC]',
    description: 'An ISO-8601 timestamp with a timezone extension (RFC 9557 format).',
  });

export const zodTemporalTimeZone = z
  .instanceof(Temporal.TimeZone)
  .or(z.string().transform((s) => Temporal.TimeZone.from(s)));

export const zodTemporalDuration = z
  .instanceof(Temporal.Duration)
  .or(z.string().transform((s) => Temporal.Duration.from(s)));

export const isTemporalPlainDate = (value: unknown): value is Temporal.PlainDate => value instanceof Temporal.PlainDate;
export const isTemporalZonedDateTime = (value: unknown): value is Temporal.ZonedDateTime =>
  value instanceof Temporal.ZonedDateTime;
export const isTemporalInstant = (value: unknown): value is Temporal.Instant => value instanceof Temporal.Instant;
export const isTemporal = (value: unknown): value is Temporal.PlainDate | Temporal.ZonedDateTime | Temporal.Instant =>
  isTemporalPlainDate(value) || isTemporalZonedDateTime(value) || isTemporalInstant(value);

/**
 * Converts certain Temporal objects to legacy JavaScript Date objects:
 * - Temporal.Instant is converted directly to a Date object representing the same instant in time.
 * - Temporal.PlainDate is converted to a Date object representing 00:00 UTC time on the PlainDate
 * - Temporal.ZonedDateTime is converted to a Date object representing the given ZDT's instant in time.
 */
export function toLegacyDate(t: Temporal.Instant | Temporal.PlainDate | Temporal.ZonedDateTime): Date {
  switch (true) {
    case isTemporalInstant(t):
      return new Date(t.epochMilliseconds);
    case isTemporalPlainDate(t):
      return new Date(t.toZonedDateTime('UTC').toInstant().epochMilliseconds);
    case isTemporalZonedDateTime(t):
      return new Date(t.toInstant().epochMilliseconds);
    default:
      assertNever(t);
  }
}

/**
 * Wrapper around the `toTemporalInstant` polyfill that allows us to use it as a function.
 *
 * @todo deprecate and remove this when native Date.prototype.toTemporalInstant lands
 */
export const toTemporalInstant = (d: Date) => toTemporalInstantPolyfill.apply(d);

/**
 * @description Checks if the first instant or zoned date time is after the second instant or zoned date time.
 * @param dateA - The first instant or zoned date time to compare.
 * @param dateB - The second instant or zoned date time to compare.
 */
export function isAfter(dateA: Temporal.PlainDate, dateB: Temporal.PlainDate): boolean;
export function isAfter(
  dateA: Temporal.Instant | Temporal.ZonedDateTime,
  dateB: Temporal.Instant | Temporal.ZonedDateTime,
): boolean;
export function isAfter(
  dateA: Temporal.Instant | Temporal.ZonedDateTime | Temporal.PlainDate,
  dateB: Temporal.Instant | Temporal.ZonedDateTime | Temporal.PlainDate,
): boolean {
  if (dateA instanceof Temporal.PlainDate && dateB instanceof Temporal.PlainDate) {
    return Temporal.PlainDate.compare(dateA, dateB) > 0;
  }
  if (dateA instanceof Temporal.PlainDate || dateB instanceof Temporal.PlainDate) {
    throw new Error('Cannot compare PlainDate with Instant or ZonedDateTime');
  }

  const dateAInstant = dateA instanceof Temporal.Instant ? dateA : dateA.toInstant();
  const dateBInstant = dateB instanceof Temporal.Instant ? dateB : dateB.toInstant();
  return dateAInstant.epochMilliseconds > dateBInstant.epochMilliseconds;
}

/**
 * @description Checks if the first instant or zoned date time is after or equal to the second instant or zoned date time.
 * @param dateA - The first instant or zoned date time to compare.
 * @param dateB - The second instant or zoned date time to compare.
 * @returns Whether the first instant or zoned date time is after or equal to the second instant or zoned date time.
 */
export function isAfterOrEqualTo(dateA: Temporal.PlainDate, dateB: Temporal.PlainDate): boolean;
export function isAfterOrEqualTo(
  dateA: Temporal.Instant | Temporal.ZonedDateTime,
  dateB: Temporal.Instant | Temporal.ZonedDateTime,
): boolean;
export function isAfterOrEqualTo(
  dateA: Temporal.Instant | Temporal.ZonedDateTime | Temporal.PlainDate,
  dateB: Temporal.Instant | Temporal.ZonedDateTime | Temporal.PlainDate,
): boolean {
  if (dateA instanceof Temporal.PlainDate && dateB instanceof Temporal.PlainDate) {
    return Temporal.PlainDate.compare(dateA, dateB) >= 0;
  }
  if (dateA instanceof Temporal.PlainDate || dateB instanceof Temporal.PlainDate) {
    throw new Error('Cannot compare PlainDate with Instant or ZonedDateTime');
  }

  const dateAInstant = dateA instanceof Temporal.Instant ? dateA : dateA.toInstant();
  const dateBInstant = dateB instanceof Temporal.Instant ? dateB : dateB.toInstant();
  return dateAInstant.epochMilliseconds >= dateBInstant.epochMilliseconds;
}

/**
 * @description Checks if the first instant or zoned date time is before the second instant or zoned date time.
 * @param dateA - The first instant or zoned date time to compare.
 * @param dateB - The second instant or zoned date time to compare.
 */
export function isBefore(dateA: Temporal.PlainDate, dateB: Temporal.PlainDate): boolean;
export function isBefore(
  dateA: Temporal.Instant | Temporal.ZonedDateTime,
  dateB: Temporal.Instant | Temporal.ZonedDateTime,
): boolean;
export function isBefore(
  dateA: Temporal.Instant | Temporal.ZonedDateTime | Temporal.PlainDate,
  dateB: Temporal.Instant | Temporal.ZonedDateTime | Temporal.PlainDate,
): boolean {
  if (dateA instanceof Temporal.PlainDate && dateB instanceof Temporal.PlainDate) {
    return Temporal.PlainDate.compare(dateA, dateB) < 0;
  }
  if (dateA instanceof Temporal.PlainDate || dateB instanceof Temporal.PlainDate) {
    throw new Error('Cannot compare PlainDate with Instant or ZonedDateTime');
  }

  const dateAInstant = dateA instanceof Temporal.Instant ? dateA : dateA.toInstant();
  const dateBInstant = dateB instanceof Temporal.Instant ? dateB : dateB.toInstant();
  return dateAInstant.epochMilliseconds < dateBInstant.epochMilliseconds;
}

/**
 *
 * @param dateA - The first instant or zoned date time to compare.
 * @param dateB - The second instant or zoned date time to compare.
 * @returns Whether the first instant or zoned date time is before or equal to the second instant or zoned date time.
 */
export function isBeforeOrEqualTo(dateA: Temporal.PlainDate, dateB: Temporal.PlainDate): boolean;
export function isBeforeOrEqualTo(
  dateA: Temporal.Instant | Temporal.ZonedDateTime,
  dateB: Temporal.Instant | Temporal.ZonedDateTime,
): boolean;
export function isBeforeOrEqualTo(
  dateA: Temporal.Instant | Temporal.ZonedDateTime | Temporal.PlainDate,
  dateB: Temporal.Instant | Temporal.ZonedDateTime | Temporal.PlainDate,
): boolean {
  if (dateA instanceof Temporal.PlainDate && dateB instanceof Temporal.PlainDate) {
    return Temporal.PlainDate.compare(dateA, dateB) <= 0;
  }
  if (dateA instanceof Temporal.PlainDate || dateB instanceof Temporal.PlainDate) {
    throw new Error('Cannot compare PlainDate with Instant or ZonedDateTime');
  }

  const dateAInstant = dateA instanceof Temporal.Instant ? dateA : dateA.toInstant();
  const dateBInstant = dateB instanceof Temporal.Instant ? dateB : dateB.toInstant();
  return dateAInstant.epochMilliseconds <= dateBInstant.epochMilliseconds;
}
/**
 * Converts a {@link Date} object (or a {@link Temporal.PlainDate}) with no time component to a {@link Temporal.PlainDate} object.
 *
 * @throws {Error} if the Date object has a time component other than 00:00:00 UTC and errorOnNonZeroTime is true
 */
export function jsPlainDateToTemporalPlainDate(
  date: Date | Temporal.PlainDate,
  { errorOnNonZeroTime = false }: { errorOnNonZeroTime?: boolean } = {},
): Temporal.PlainDate {
  if (date instanceof Temporal.PlainDate) {
    return date;
  }
  const zdt = toTemporalInstant(date).toZonedDateTimeISO('UTC');
  const zdtPlainTime = zdt.toPlainTime();
  if (errorOnNonZeroTime && !zdtPlainTime.equals(BEGINNING_OF_DAY)) {
    throw new Error(`jsPlainDateToTemporalPlainDate only supports Dates with a 00:00:00 UTC time, got ${zdtPlainTime}`);
  }
  return zdt.toPlainDate();
}

/**
 * Converts a {@link Date} object with a time component to a
 * {@link Temporal.PlainDate} object.
 *
 * Because a particular point in time may resolve to different dates in
 * different time zones, you must provide the time zone you wish to use for the
 * computation.
 *
 * If you want to convert a plain date stored as a Date object, @see {@link jsPlainDateToTemporalPlainDate} instead.
 */
export function jsDateTimeToTemporalPlainDate(d: Date, timeZone: Temporal.TimeZoneLike): Temporal.PlainDate {
  const zdt = toTemporalInstant(d).toZonedDateTimeISO(timeZone);
  return zdt.toPlainDate();
}

/**
 * @description Converts a {@link Temporal.PlainDate} object to a {@link Date} object.
 *
 */
export function temporalPlainDateToJsPlainDate(d: Temporal.PlainDate): Date {
  return new Date(d.toString());
}

export function minDate<T = Temporal.PlainDate | Temporal.PlainDateTime | Temporal.ZonedDateTime | Temporal.Instant>(
  left: T,
  right: T,
): T {
  if (left instanceof Temporal.PlainDate && right instanceof Temporal.PlainDate) {
    return Temporal.PlainDate.compare(left, right) < 0 ? left : right;
  }
  if (left instanceof Temporal.PlainDateTime && right instanceof Temporal.PlainDateTime) {
    return Temporal.PlainDateTime.compare(left, right) < 0 ? left : right;
  }
  if (left instanceof Temporal.ZonedDateTime && right instanceof Temporal.ZonedDateTime) {
    return Temporal.ZonedDateTime.compare(left, right) < 0 ? left : right;
  }
  if (left instanceof Temporal.Instant && right instanceof Temporal.Instant) {
    return Temporal.Instant.compare(left, right) < 0 ? left : right;
  }
  throw new Error('Cannot compare different types');
}
